import React from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { debounce } from 'lodash';
import getDisplayName from 'react-display-name';

import { type IHostProps } from '@wix/yoshi-flow-editor';

export type WithLayoutUpdate = {
  host: IHostProps;
};

export const withLayoutUpdate = <P extends WithLayoutUpdate>(
  WrappedComponent: React.ComponentType<P>,
) => {
  class WithLayoutUpdateComponent extends React.Component<P> {
    static displayName = `withLayoutUpdate(${getDisplayName(WrappedComponent)})`;

    node?: HTMLElement | null;

    observer?: MutationObserver;

    lastHeight?: number;

    componentDidMount() {
      this.node = document.getElementById(this.props.host.id);
      if (!this.node) {
        return;
      }

      this.node.style.minWidth = '0';
      this.observer = new MutationObserver(this.updateLayoutIfNeeded);
      this.observer.observe(this.node, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });
      this.lastHeight = this.node.offsetHeight;

      window.addEventListener('resize', this.updateLayoutIfNeededDebounced);
      window.addEventListener('transitionend', this.updateLayoutIfNeeded);
    }

    componentWillUnmount() {
      if (!this.node) {
        return;
      }

      this.observer?.disconnect();
      window.removeEventListener('resize', this.updateLayoutIfNeededDebounced);
      window.removeEventListener('transitionend', this.updateLayoutIfNeeded);
    }

    updateLayoutIfNeeded = () => {
      if (this.node && this.lastHeight !== this.node.offsetHeight) {
        this.lastHeight = this.node.offsetHeight;
        this.props.host.updateLayout();
      }
    };

    updateLayoutIfNeededDebounced = debounce(this.updateLayoutIfNeeded, 100, {
      leading: true,
    });

    render() {
      return <WrappedComponent {...this.props} />;
    }
  }

  hoistNonReactStatics(WithLayoutUpdateComponent, WrappedComponent);

  return WithLayoutUpdateComponent;
};
